import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ClientStatus {
  id: string;
  code: string;
  beschreibung: string;
  color: string;
  bg_color: string;
  text_color: string;
  ring_color: string;
  dark_bg_color: string;
  dark_text_color: string;
  dark_ring_color: string;
  sort_order: number;
}

export interface ClientStatusStammdaten {
  client_status: ClientStatus[];
}

export interface ClientStatusResponse {
  result: number;
  data: ClientStatusStammdaten;
  sec: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClientStatusService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Get all client statuses
   */
  getClientStatuses(): Observable<ClientStatusResponse> {
    return this.http.get<ClientStatusResponse>(`${this.apiUrl}/client-status-stammdaten`);
  }

  /**
   * Initialize client statuses (first time setup)
   */
  initClientStatuses(): Observable<ClientStatusResponse> {
    return this.http.get<ClientStatusResponse>(`${this.apiUrl}/client-status-stammdaten-init`);
  }

  /**
   * Sync/update client statuses
   */
  syncClientStatuses(clientStatusStammdaten: ClientStatusStammdaten): Observable<ClientStatusResponse> {
    return this.http.post<ClientStatusResponse>(`${this.apiUrl}/client-status-stammdaten`, {
      client_status_stammdaten: clientStatusStammdaten
    });
  }

  /**
   * Add a new client status
   */
  addClientStatus(status: Omit<ClientStatus, 'id'>): Observable<ClientStatusResponse> {
    return this.getClientStatuses().pipe(
      map(response => {
        const currentData = response.data;
        const newStatus: ClientStatus = {
          ...status,
          id: this.generateUUID()
        };
        
        currentData.client_status.push(newStatus);
        return currentData;
      }),
      switchMap(updatedData => this.syncClientStatuses(updatedData))
    );
  }

  /**
   * Update an existing client status
   */
  updateClientStatus(id: string, status: Partial<ClientStatus>): Observable<ClientStatusResponse> {
    return this.getClientStatuses().pipe(
      map(response => {
        const currentData = response.data;
        const statusIndex = currentData.client_status.findIndex(s => s.id === id);
        
        if (statusIndex !== -1) {
          currentData.client_status[statusIndex] = {
            ...currentData.client_status[statusIndex],
            ...status
          };
        }
        
        return currentData;
      }),
      switchMap(updatedData => this.syncClientStatuses(updatedData))
    );
  }

  /**
   * Delete a client status
   */
  deleteClientStatus(id: string): Observable<ClientStatusResponse> {
    return this.getClientStatuses().pipe(
      map(response => {
        const currentData = response.data;
        currentData.client_status = currentData.client_status.filter(s => s.id !== id);
        return currentData;
      }),
      switchMap(updatedData => this.syncClientStatuses(updatedData))
    );
  }

  /**
   * Get status by code
   */
  getStatusByCode(code: string): Observable<ClientStatus | null> {
    return this.getClientStatuses().pipe(
      map(response => {
        const status = response.data.client_status.find(s => s.code === code);
        return status || null;
      })
    );
  }

  /**
   * Get status by ID
   */
  getStatusById(id: string): Observable<ClientStatus | null> {
    return this.getClientStatuses().pipe(
      map(response => {
        const status = response.data.client_status.find(s => s.id === id);
        return status || null;
      })
    );
  }

  /**
   * Generate a UUID for new statuses
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
